import { FC, useRef, useState } from "react";

import Image from "next/image";
import { FormattedMessage, FormattedDate } from "react-intl";

import { Avatar, AvatarImage, AvatarFallback } from "@/components/atoms/Avatar";
import { Text } from "@/components/atoms/Text";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  DotNavigation,
  type CarouselApi,
} from "@/components/molecules/Carousel";
import Star from "@/icons/__generated__/StarFilled/StarFilled20";
import { useLazyLoadingCarouselImages } from "@/lib/hooks/use-lazy-loading-carousel-images";
import { IStructuredReview } from "~/types/api-types";
import { cloudinaryImageLoader } from "~/utils/image-loaders";

interface UgcCarouselCardProps {
  review: IStructuredReview & {
    UGCs: { imageId: string; description: string }[];
  };
}

export const UgcCarouselCard: FC<UgcCarouselCardProps> = ({ review }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [api, setApi] = useState<CarouselApi>();
  const { isVisible, loadedImages } = useLazyLoadingCarouselImages(
    cardRef,
    review.UGCs.map((ugc) => ugc.imageId),
    api,
  );

  return (
    <div ref={cardRef}>
      <Carousel
        className="tw-group"
        opts={{
          align: "start",
          loop: false,
        }}
        setApi={setApi}
      >
        <CarouselContent>
          {review.UGCs.map((ugc, index) => (
            <CarouselItem key={`ugc-slide-${index}`}>
              <div className="tw-flex tw-relative">
                {isVisible && loadedImages.has(ugc.imageId) && (
                  <Image
                    src={ugc.imageId}
                    alt={ugc.description}
                    loader={cloudinaryImageLoader}
                    width={360}
                    height={450}
                    className="tw-object-cover tw-object-center tw-aspect-[4/5] tw-w-full"
                  />
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {review.UGCs.length > 1 && (
          <>
            <div className="tw-opacity-0 tw-text-base-white group-hover:tw-opacity-65">
              <CarouselPrevious />
              <CarouselNext />
            </div>
            <div className="tw--mt-16">
              <DotNavigation
                className="tw-mb-0"
                totalItems={review.UGCs.length}
              />
            </div>
          </>
        )}
      </Carousel>
      <div className="tw-flex tw-justify-between tw-pt-16">
        <div className="tw-flex">
          <div className="tw-mr-16">
            <Avatar size={32}>
              <AvatarImage
                src={`https://img.peerspace.com/image/upload/c_crop,g_custom/f_auto,fl_progressive/c_scale,dpr_auto,h_32,w_32/${review.guestAvatarId}`}
              />
              <AvatarFallback />
            </Avatar>
          </div>
          <div>
            <Text element="h5" typeface="body-2-m">
              {review.activityType}
            </Text>
            <Text element="p" typeface="body-2">
              {review.guestName.first ? (
                <FormattedMessage
                  id="listing.ugc_carousel.submitted_by"
                  defaultMessage="Submitted by {name}"
                  values={{
                    name: `${review.guestName?.first ?? "Guest"} ${review.guestName?.last ?? ""}`,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="listing.ugc_carousel.by_anonymous"
                  defaultMessage="By Anonymous"
                />
              )}
            </Text>
            <Text
              typeface="body-1"
              element="p"
              className="tw-text-gray-600 tw-mb-0"
            >
              <FormattedDate
                value={review.bookingDate}
                year="numeric"
                month="long"
              />
            </Text>
          </div>
        </div>
        <div className="tw-h-full tw-flex">
          <Star className="star margin-sm-right" width={20} height={20} />
          <Text typeface="body-2" element="p">
            {review.reviewStars?.toFixed(1)}
          </Text>
        </div>
      </div>
    </div>
  );
};
